import { decodeJsonObject, postLocalJson } from "./localJsonTransport";
// Actor-in-World view prefs through the local server's grant door (RAIL-6,
// principal ruling 2026-08-26): the durable rail fold lives in the World
// authority's viewPrefs row — the same row the cockpit browser writes — so
// the room's shape follows the Actor across doors. The browser names a
// World; the Actor arrives from the deposited grant, never from here.

// RM9: a session's deck as the door holds it — the ordered boards open in
// its session-view, each with its kind, target, opaque payload, and pin.
export interface SessionDeckBoardWire {
  readonly id: string;
  readonly kind: string;
  readonly target?: string;
  readonly payload?: string;
  readonly pin?: {
    readonly revisionKey?: string;
    readonly digest?: string;
    readonly openedAt: number;
  };
}
export interface SessionDeckWire {
  readonly sessionId: string;
  readonly activeBoardId?: string;
  readonly boards: ReadonlyArray<SessionDeckBoardWire>;
  readonly updatedAt?: number;
}

export type WorldViewPrefsOutcome =
  | {
      readonly ok: true;
      readonly collapsed: ReadonlyArray<string>;
      readonly sessionDecks: ReadonlyArray<SessionDeckWire>;
    }
  | { readonly ok: false; readonly kind: string; readonly reason: string };

function refusal(response: Response, body: Record<string, unknown> | null): WorldViewPrefsOutcome {
  return {
    ok: false,
    kind: typeof body?.["kind"] === "string" ? body["kind"] : `http_${response.status}`,
    reason:
      typeof body?.["reason"] === "string"
        ? body["reason"]
        : "The Actor-in-World view prefs are unavailable.",
  };
}

function parsedCollapsed(value: unknown): ReadonlyArray<string> | null {
  if (!Array.isArray(value) || value.some((item) => typeof item !== "string")) return null;
  return value as ReadonlyArray<string>;
}

function parsedDeck(value: unknown): SessionDeckWire | null {
  if (value === null || typeof value !== "object" || Array.isArray(value)) return null;
  const d = value as Record<string, unknown>;
  if (typeof d["sessionId"] !== "string" || !Array.isArray(d["boards"])) return null;
  const boards: SessionDeckBoardWire[] = [];
  for (const raw of d["boards"]) {
    if (raw === null || typeof raw !== "object" || Array.isArray(raw)) return null;
    const b = raw as Record<string, unknown>;
    if (typeof b["id"] !== "string" || typeof b["kind"] !== "string") return null;
    const pin = b["pin"];
    boards.push({
      id: b["id"],
      kind: b["kind"],
      ...(typeof b["target"] === "string" ? { target: b["target"] } : {}),
      ...(typeof b["payload"] === "string" ? { payload: b["payload"] } : {}),
      ...(pin !== null &&
      typeof pin === "object" &&
      typeof (pin as Record<string, unknown>)["openedAt"] === "number"
        ? { pin: pin as NonNullable<SessionDeckBoardWire["pin"]> }
        : {}),
    });
  }
  return {
    sessionId: d["sessionId"],
    ...(typeof d["activeBoardId"] === "string" ? { activeBoardId: d["activeBoardId"] } : {}),
    boards,
    ...(typeof d["updatedAt"] === "number" ? { updatedAt: d["updatedAt"] } : {}),
  };
}

function parsedDecks(value: unknown): ReadonlyArray<SessionDeckWire> | null {
  // A server deployed before RM9 answers no decks: the same meaning as none held.
  if (value === undefined) return [];
  if (!Array.isArray(value)) return null;
  const decks: SessionDeckWire[] = [];
  for (const entry of value) {
    const deck = parsedDeck(entry);
    if (deck === null) return null;
    decks.push(deck);
  }
  return decks;
}

async function call(
  path: string,
  payload: Record<string, unknown>,
  fetchImpl: typeof fetch,
): Promise<WorldViewPrefsOutcome> {
  let response: Response;
  try {
    response = await postLocalJson(path, payload, fetchImpl);
  } catch {
    return { ok: false, kind: "transport", reason: "The local Cleo server is unreachable." };
  }
  const body = await decodeJsonObject(response);
  if (!response.ok || body?.["ok"] !== true) return refusal(response, body);
  const collapsed = parsedCollapsed(body["collapsed"]);
  const sessionDecks = parsedDecks(body["sessionDecks"]);
  return collapsed === null || sessionDecks === null
    ? { ok: false, kind: "transport", reason: "The local Cleo server returned invalid view prefs." }
    : { ok: true, collapsed, sessionDecks };
}

export type SessionDeckSaveOutcome =
  | { readonly ok: true; readonly deck: SessionDeckWire }
  | { readonly ok: false; readonly kind: string; readonly reason: string };

/** RM9: save one session's deck through the door; success is the door's readback. */
export async function saveWorldSessionDeck(
  worldKey: string,
  sessionId: string,
  deck: { readonly boards: ReadonlyArray<SessionDeckBoardWire>; readonly activeBoardId?: string },
  fetchImpl: typeof fetch = fetch,
): Promise<SessionDeckSaveOutcome> {
  let response: Response;
  try {
    response = await postLocalJson(
      "/api/neucleos/world/view-prefs/session-deck",
      {
        worldKey,
        sessionId,
        boards: deck.boards,
        ...(deck.activeBoardId === undefined ? {} : { activeBoardId: deck.activeBoardId }),
      },
      fetchImpl,
    );
  } catch {
    return { ok: false, kind: "transport", reason: "The local Cleo server is unreachable." };
  }
  const body = await decodeJsonObject(response);
  if (!response.ok || body?.["ok"] !== true) {
    const refused = refusal(response, body);
    return refused.ok
      ? { ok: false, kind: "transport", reason: "The session-deck save was refused." }
      : refused;
  }
  const stored = parsedDeck(body["deck"]);
  return stored === null || stored.sessionId !== sessionId
    ? {
        ok: false,
        kind: "transport",
        reason: "The local Cleo server returned an invalid session deck.",
      }
    : { ok: true, deck: stored };
}

export async function readWorldViewPrefs(
  worldKey: string,
  fetchImpl: typeof fetch = fetch,
  sessionId?: string,
): Promise<WorldViewPrefsOutcome> {
  return call(
    "/api/neucleos/world/view-prefs",
    { worldKey, ...(sessionId === undefined ? {} : { sessionId }) },
    fetchImpl,
  );
}

export async function saveWorldViewPrefs(
  worldKey: string,
  collapsed: ReadonlyArray<string>,
  fetchImpl: typeof fetch = fetch,
): Promise<WorldViewPrefsOutcome> {
  return call("/api/neucleos/world/view-prefs/save", { worldKey, collapsed }, fetchImpl);
}
